import React, {useEffect, useMemo, useRef, useState} from "react";
import {TravelDuration, TravelDurationMap} from "../../models/TravelDuration";
import {useTranslation} from "../../i18n/LanguageProvider";

type NullableDate = Date | null;

export interface DateRangeCalendarProps {
    from: NullableDate;
    to: NullableDate;
    onChange: (from: NullableDate, to: NullableDate) => void;
    minDate?: Date;
    maxDate?: Date;
    locale?: string;
    weekStartsOnMonday?: boolean;
    travelDuration: TravelDuration;
}

const addMonths = (d: Date, n: number) =>
    new Date(d.getFullYear(), d.getMonth() + n, 1);
const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
const isBefore = (a: Date, b: Date) =>
    startOfDay(a).getTime() < startOfDay(b).getTime();

function monthMatrix(base: Date, weekStartsOnMonday: boolean) {
    const first = startOfMonth(base);
    const last = endOfMonth(base);
    let startWeekday = first.getDay(); // 0=Sun...6=Sat
    if (weekStartsOnMonday) startWeekday = (startWeekday + 6) % 7; // 0=Mon
    const days: (Date | null)[] = [];
    for (let i = 0; i < startWeekday; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
        days.push(new Date(base.getFullYear(), base.getMonth(), d));
    }
    while (days.length % 7 !== 0) days.push(null);
    const rows: (Date | null)[][] = [];
    for (let i = 0; i < days.length; i += 7) {
        rows.push(days.slice(i, i + 7));
    }
    return rows;
}

const weekdayLabels = (locale: string, weekStartsOnMonday: boolean) => {
    const base = new Date(2021, 7, 2); // Montag
    const days = [...Array(7)].map((_, i) => {
        const d = new Date(base);
        d.setDate(base.getDate() + i);
        return d
            .toLocaleDateString(locale, {weekday: "short"})
            .toUpperCase()
            .slice(0, 3);
    });
    return weekStartsOnMonday ? days : [days[6], ...days.slice(0, 6)];
};

const DateRangeCalendar: React.FC<DateRangeCalendarProps> = ({
                                                                 from,
                                                                 to,
                                                                 onChange,
                                                                 minDate,
                                                                 maxDate,
                                                                 locale = "en-US",
                                                                 weekStartsOnMonday = true,
                                                                 travelDuration,
                                                             }) => {
    const {t} = useTranslation();
    const today = useMemo(() => startOfDay(new Date()), []);
    const defaultMaxDate = useMemo(() => addMonths(today, 6), [today]); // ✅ max 6 Monate
    const [cursor, setCursor] = useState<Date>(() => startOfMonth(from ?? today));
    const [tempHover, setTempHover] = useState<NullableDate>(null);

    const {minDays} = TravelDurationMap[travelDuration];
    const effectiveMaxDate = maxDate ?? defaultMaxDate;

    // swipe handling
    const swipeRef = useRef<HTMLDivElement>(null);
    const startX = useRef<number | null>(null);

    useEffect(() => {
        const el = swipeRef.current;
        if (!el) return;

        const onStart = (x: number) => (startX.current = x);
        const onEnd = (x: number) => {
            if (startX.current === null) return;
            const dx = x - startX.current;
            startX.current = null;
            const threshold = 60;
            if (dx > threshold) setCursor((c) => c > startOfMonth(minDate ?? today) ? addMonths(c, -1) : c);
            else if (dx < -threshold) setCursor((c) => c < startOfMonth(effectiveMaxDate) ? addMonths(c, 1) : c);
        };

        const touchStart = (e: TouchEvent) => onStart(e.touches[0].clientX);
        const touchEnd = (e: TouchEvent) => onEnd(e.changedTouches[0].clientX);
        const mouseDown = (e: MouseEvent) => onStart(e.clientX);
        const mouseUp = (e: MouseEvent) => onEnd(e.clientX);

        el.addEventListener("touchstart", touchStart, {passive: true});
        el.addEventListener("touchend", touchEnd);
        el.addEventListener("mousedown", mouseDown);
        window.addEventListener("mouseup", mouseUp);

        return () => {
            el.removeEventListener("touchstart", touchStart);
            el.removeEventListener("touchend", touchEnd);
            el.removeEventListener("mousedown", mouseDown);
            window.removeEventListener("mouseup", mouseUp);
        };
    }, [minDate, today, effectiveMaxDate]);

    const weeks = weekdayLabels(locale, weekStartsOnMonday);
    const months = useMemo(() => [cursor, addMonths(cursor, 1)], [cursor]);

    const inRange = (d: Date) => {
        if (!from || (!to && !tempHover)) return false;
        const end = to ?? tempHover!;
        const a = isBefore(from, end) ? from : end;
        const b = isBefore(from, end) ? end : from;
        return d >= startOfDay(a) && d <= startOfDay(b);
    };

    const disabled = (d: Date) => {
        if (minDate && d < startOfDay(minDate)) return true;
        if (effectiveMaxDate && d > startOfDay(effectiveMaxDate)) return true;
        return false;
    };

    const handleSelect = (d: Date) => {
        if (disabled(d)) return;
        setTempHover(null);

        if (!from || (from && to)) {
            onChange(startOfDay(d), null);
            return;
        }

        let newFrom = from;
        let newTo = startOfDay(d);

        if (isBefore(newTo, newFrom)) {
            newFrom = startOfDay(d);
            newTo = startOfDay(from);
        }

        // Check minimum count of days
        const diffDays =
            (newTo.getTime() - newFrom.getTime()) / (1000 * 60 * 60 * 24) + 1;
        if (diffDays < minDays) {
            const adjustedTo = new Date(newFrom);
            adjustedTo.setDate(newFrom.getDate() + minDays - 1);

            if (effectiveMaxDate && adjustedTo > effectiveMaxDate) {
                return;
            }
            newTo = adjustedTo;
        }

        onChange(newFrom, newTo);
    };

    return (
        <div ref={swipeRef} className="w-full select-none" aria-label={t("calendar.aria")}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={() => setCursor(addMonths(cursor, -1))}
                    className="min-h-[44px] min-w-[36px] rounded-lg px-2 py-1 text-[#4B4B4B] hover:bg-[#F3EEFF] transition"
                    aria-label={t("calendar.previousMonth")}
                    disabled={cursor <= startOfMonth(minDate ?? today)}
                >
                    ‹
                </button>
                <div className="flex gap-8 text-center">
                    {months.map((m, i) => (
                        <div key={i} className={i === 1 ? "hidden md:block min-w-0" : "min-w-0"}>
                            <div className="text-base font-semibold tracking-wide text-[#4B4B4B]">
                                {m.toLocaleDateString(locale, {month: "long", year: "numeric"})}
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    onClick={() => setCursor(addMonths(cursor, +1))}
                    className="min-h-[44px] min-w-[36px] rounded-lg px-2 py-1 text-[#4B4B4B] hover:bg-[#F3EEFF] transition"
                    aria-label={t("calendar.nextMonth")}
                    disabled={cursor >= startOfMonth(effectiveMaxDate)}
                >
                    ›
                </button>
            </div>

            {/* Two-month grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {months.map((m, idx) => {
                    const rows = monthMatrix(m, weekStartsOnMonday);
                    return (
                        <div key={idx} className={idx === 1 ? "hidden md:block" : "min-w-0"}>
                            <div className="grid grid-cols-7 gap-1 mb-2 text-xs font-semibold text-[#9A9A9A]">
                                {weeks.map((w) => (
                                    <div key={w} className="text-center min-w-0">{w}</div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                                {rows.flat().map((cell, i) => {
                                    if (!cell) return <div key={i} className="h-11 min-w-0"/>;
                                    const isFrom = !!from && isSameDay(cell, from);
                                    const isTo = !!to && isSameDay(cell, to);
                                    const isToday = isSameDay(cell, today);
                                    const isIn = inRange(cell);
                                    const isDisabled = disabled(cell);

                                    return (
                                        <button
                                            key={i}
                                            onClick={() => handleSelect(cell)}
                                            onMouseEnter={() => {
                                                if (from && !to) setTempHover(cell);
                                            }}
                                            onMouseLeave={() => setTempHover(null)}
                                            disabled={isDisabled}
                                            aria-label={cell.toLocaleDateString(locale, {dateStyle: "full"})}
                                            aria-pressed={isFrom || isTo ? true : undefined}
                                            className={[
                                                "h-11 w-full min-w-0 flex items-center justify-center rounded-xl text-sm transition relative",
                                                "focus-visible:outline-accent",
                                                isDisabled
                                                    ? "text-[#C6C6C6] cursor-not-allowed"
                                                    : isFrom || isTo
                                                        ? "bg-[#712FFF] text-white font-semibold"
                                                        : isIn
                                                            ? "bg-[#E9E2FF] hover:bg-[#D5CCFF]"
                                                            : "hover:bg-[#F3EEFF]",
                                            ].join(" ")}
                                        >
                                            {cell.getDate()}
                                            {isToday && !(isFrom || isTo) && (
                                                <span
                                                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px]">•</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default DateRangeCalendar;
